
import { Button } from "@/components/ui/button";
import { Bold, Italic, List } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { useState, useRef, useEffect } from "react";

type TextFormattingToolbarProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

export function TextFormattingToolbar({ value, onChange, className }: TextFormattingToolbarProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());

  // Update active formats based on cursor position
  useEffect(() => {
    updateActiveFormats();
  }, [selectionStart, selectionEnd, value]);

  const updateActiveFormats = () => {
    const formats = new Set<string>();
    
    if (selectionStart !== selectionEnd) {
      const selectedText = value.substring(selectionStart, selectionEnd);
      if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
        formats.add('bold');
      }
      if (selectedText.startsWith('*') && selectedText.endsWith('*') && !selectedText.startsWith('**')) {
        formats.add('italic');
      }
    }
    
    setActiveFormats(formats);
  };

  const handleFormat = (format: 'bold' | 'italic' | 'list') => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let newText = value;
    let newCursorPos = end;

    switch (format) {
      case 'bold':
        if (selectedText) {
          // Check if already bold
          if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
            // Remove bold formatting
            const unformattedText = selectedText.slice(2, -2);
            newText = value.substring(0, start) + unformattedText + value.substring(end);
            newCursorPos = start + unformattedText.length;
          } else {
            // Add bold formatting
            newText = value.substring(0, start) + `**${selectedText}**` + value.substring(end);
            newCursorPos = end + 4;
          }
        } else {
          // Insert bold markers and position cursor between them
          newText = value.substring(0, start) + `****` + value.substring(end);
          newCursorPos = start + 2;
        }
        break;
      case 'italic':
        if (selectedText) {
          // Check if already italic (but not bold)
          if (selectedText.startsWith('*') && selectedText.endsWith('*') && !selectedText.startsWith('**')) {
            // Remove italic formatting
            const unformattedText = selectedText.slice(1, -1);
            newText = value.substring(0, start) + unformattedText + value.substring(end);
            newCursorPos = start + unformattedText.length;
          } else {
            // Add italic formatting
            newText = value.substring(0, start) + `*${selectedText}*` + value.substring(end);
            newCursorPos = end + 2;
          }
        } else {
          // Insert italic markers and position cursor between them
          newText = value.substring(0, start) + `**` + value.substring(end);
          newCursorPos = start + 1;
        }
        break;
      case 'list':
        if (selectedText) {
          // If multi-line selection, add bullet to each line
          if (selectedText.includes('\n')) {
            const lines = selectedText.split('\n');
            const formattedLines = lines.map(line => line.trim() ? `• ${line}` : line).join('\n');
            newText = value.substring(0, start) + formattedLines + value.substring(end);
            newCursorPos = start + formattedLines.length;
          } else {
            // Single line selected, add bullet
            newText = value.substring(0, start) + `• ${selectedText}` + value.substring(end);
            newCursorPos = end + 2;
          }
        } else {
          // No text selected, insert bullet point
          const beforeCursor = value.substring(0, start);
          const afterCursor = value.substring(end);
          
          // Check if we're at the start of a line
          const isStartOfLine = start === 0 || value.charAt(start - 1) === '\n';
          
          if (isStartOfLine) {
            newText = beforeCursor + '• ' + afterCursor;
            newCursorPos = start + 2;
          } else {
            newText = beforeCursor + '\n• ' + afterCursor;
            newCursorPos = start + 3;
          }
        }
        break;
    }

    onChange(newText);

    // Set focus back to textarea and restore cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleSelectionChange = () => {
    if (textareaRef.current) {
      setSelectionStart(textareaRef.current.selectionStart);
      setSelectionEnd(textareaRef.current.selectionEnd);
    }
  };

  return (
    <div className={className}>
      <div className="flex space-x-1 mb-2">
        <Button 
          type="button" 
          variant={activeFormats.has('bold') ? "default" : "outline"}
          size="sm" 
          onClick={() => handleFormat('bold')}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
          <span className="sr-only">Bold</span>
        </Button>
        <Button 
          type="button" 
          variant={activeFormats.has('italic') ? "default" : "outline"}
          size="sm" 
          onClick={() => handleFormat('italic')}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
          <span className="sr-only">Italic</span>
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={() => handleFormat('list')}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
          <span className="sr-only">Bullet List</span>
        </Button>
      </div>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={handleSelectionChange}
        onClick={handleSelectionChange}
        onKeyUp={handleSelectionChange}
        placeholder="Enter your prompt content"
        className="min-h-[200px] w-full"
      />
    </div>
  );
}
